/**
 * @fileoverview WebContainer-style File System API for Sandbox Platform SDK
 */

import { EventEmitter } from 'eventemitter3'

/**
 * File tree structure matching WebContainer's interface
 */
export interface FileSystemTree {
  [key: string]: FileNode
}

/**
 * File node interface
 */
export interface FileNode {
  /** File content for files, undefined for directories */
  file?: {
    /** File contents as string or binary data */
    contents: string | Uint8Array
  }
  /** Directory contents */
  directory?: FileSystemTree
}

/**
 * File system stats
 */
export interface FileStats {
  isFile(): boolean
  isDirectory(): boolean
  isSymbolicLink(): boolean
  size: number
  mode: number
  mtime: Date
  ctime: Date
  atime: Date
}

/**
 * Directory entry
 */
export interface Dirent {
  name: string
  isFile(): boolean
  isDirectory(): boolean
  isSymbolicLink(): boolean
}

/**
 * File system watch options
 */
export interface WatchOptions {
  recursive?: boolean
  persistent?: boolean
  encoding?: 'utf8' | 'utf16le' | 'latin1' | 'base64' | 'hex'
  /** File patterns to include (glob patterns) */
  include?: string[]
  /** File patterns to exclude (glob patterns) */
  exclude?: string[]
  /** Debounce delay in milliseconds */
  debounceMs?: number
}

/**
 * File system watcher
 */
export interface FileSystemWatcher extends EventEmitter {
  /** Close the watcher */
  close(): void
  /** Reference the watcher to keep the process alive */
  ref(): FileSystemWatcher
  /** Unreference the watcher */
  unref(): FileSystemWatcher
}

/**
 * File system API matching WebContainer's interface
 */
export interface FileSystemAPI {
  // File operations
  readFile(path: string, encoding?: 'utf8' | 'utf16le' | 'latin1' | 'base64' | 'hex' | null): Promise<string | Uint8Array>

  writeFile(path: string, data: string | Uint8Array): Promise<void>

  // Directory operations
  readdir(path: string, options?: { withFileTypes?: boolean }): Promise<string[] | Dirent[]>

  mkdir(path: string, options?: { recursive?: boolean }): Promise<void>
  rmdir(path: string, options?: { recursive?: boolean }): Promise<void>

  // File/Directory status
  stat(path: string): Promise<FileStats>
  lstat(path: string): Promise<FileStats>
  exists(path: string): Promise<boolean>

  // File operations
  unlink(path: string): Promise<void>
  rename(path: string, newPath: string): Promise<void>
  copyFile(src: string, dest: string): Promise<void>

  // Watching
  watch(path: string, options?: WatchOptions): FileSystemWatcher

  // Bulk operations
  mount(tree: FileSystemTree): Promise<void>
}

/**
 * Implementation of FileSystemAPI for Sandbox Platform
 */
export class SandboxFileSystem implements FileSystemAPI {
  private httpClient: any
  private sandboxId: string
  private watchers = new Set<FileSystemWatcher>()

  constructor(httpClient: any, sandboxId: string) {
    this.httpClient = httpClient
    this.sandboxId = sandboxId
  }

  async readFile(path: string, encoding?: 'utf8' | 'utf16le' | 'latin1' | 'base64' | 'hex' | null): Promise<string | Uint8Array> {
    try {
      const response = await this.httpClient.get(
        `/api/sandbox/${this.sandboxId}/files`,
        { path }
      )

      if (!response.success) {
        throw new Error(`Failed to read file: ${response.error}`)
      }

      const content = response.data.content
      const isBase64 = response.data.encoding === 'base64'

      if (encoding === null) {
        // Return Uint8Array for binary data
        if (isBase64) {
          const binaryString = atob(content)
          const bytes = new Uint8Array(binaryString.length)
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i)
          }
          return bytes
        } else {
          // Convert string to Uint8Array
          return new TextEncoder().encode(content)
        }
      }

      // Return string
      if (isBase64) {
        return atob(content)
      }
      return content
    } catch (error) {
      throw new Error(`Failed to read file ${path}: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async writeFile(path: string, data: string | Uint8Array): Promise<void> {
    try {
      let content: string
      let encoding = 'utf8'

      if (data instanceof Uint8Array) {
        // Convert Uint8Array to base64
        const binaryString = String.fromCharCode(...data)
        content = btoa(binaryString)
        encoding = 'base64'
      } else {
        content = data
      }

      const response = await this.httpClient.put(
        `/api/sandbox/${this.sandboxId}/files`,
        {
          path,
          content,
          encoding
        }
      )

      if (!response.success) {
        throw new Error(`Failed to write file: ${response.error}`)
      }
    } catch (error) {
      throw new Error(`Failed to write file ${path}: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async readdir(path: string, options?: { withFileTypes?: boolean }): Promise<string[] | Dirent[]> {
    try {
      const response = await this.httpClient.get(
        `/api/sandbox/${this.sandboxId}/files/list`,
        { path }
      )

      if (!response.success) {
        throw new Error(`Failed to read directory: ${response.error}`)
      }

      const files = response.data.files || []

      if (options?.withFileTypes) {
        return files.map((file: any) => ({
          name: file.name,
          isFile: () => file.type === 'file',
          isDirectory: () => file.type === 'directory',
          isSymbolicLink: () => file.type === 'symlink'
        }))
      }

      return files.map((file: any) => file.name)
    } catch (error) {
      throw new Error(`Failed to read directory ${path}: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async mkdir(path: string, options: { recursive?: boolean } = {}): Promise<void> {
    try {
      const response = await this.httpClient.post(
        `/api/sandbox/${this.sandboxId}/files/mkdir`,
        {
          path,
          recursive: options.recursive || false
        }
      )

      if (!response.success) {
        throw new Error(`Failed to create directory: ${response.error}`)
      }
    } catch (error) {
      throw new Error(`Failed to create directory ${path}: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async rmdir(path: string, options: { recursive?: boolean } = {}): Promise<void> {
    try {
      const response = await this.httpClient.delete(
        `/api/sandbox/${this.sandboxId}/files`,
        {
          path,
          recursive: options.recursive || false
        }
      )

      if (!response.success) {
        throw new Error(`Failed to remove directory: ${response.error}`)
      }
    } catch (error) {
      throw new Error(`Failed to remove directory ${path}: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async stat(path: string): Promise<FileStats> {
    return this.lstat(path)
  }

  async lstat(path: string): Promise<FileStats> {
    try {
      const response = await this.httpClient.get(
        `/api/sandbox/${this.sandboxId}/files/stat`,
        { path }
      )

      if (!response.success) {
        throw new Error(`Failed to stat file: ${response.error}`)
      }

      const stats = response.data
      return {
        isFile: () => stats.type === 'file',
        isDirectory: () => stats.type === 'directory',
        isSymbolicLink: () => stats.type === 'symlink',
        size: stats.size || 0,
        mode: stats.mode || 0o644,
        mtime: new Date(stats.mtime || Date.now()),
        ctime: new Date(stats.ctime || Date.now()),
        atime: new Date(stats.atime || Date.now())
      }
    } catch (error) {
      throw new Error(`Failed to stat ${path}: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async exists(path: string): Promise<boolean> {
    try {
      await this.stat(path)
      return true
    } catch {
      return false
    }
  }

  async unlink(path: string): Promise<void> {
    try {
      const response = await this.httpClient.delete(
        `/api/sandbox/${this.sandboxId}/files`,
        { path }
      )

      if (!response.success) {
        throw new Error(`Failed to delete file: ${response.error}`)
      }
    } catch (error) {
      throw new Error(`Failed to delete file ${path}: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async rename(path: string, newPath: string): Promise<void> {
    try {
      const response = await this.httpClient.post(
        `/api/sandbox/${this.sandboxId}/files/move`,
        {
          from: path,
          to: newPath
        }
      )

      if (!response.success) {
        throw new Error(`Failed to rename file: ${response.error}`)
      }
    } catch (error) {
      throw new Error(`Failed to rename ${path} to ${newPath}: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async copyFile(src: string, dest: string): Promise<void> {
    try {
      const response = await this.httpClient.post(
        `/api/sandbox/${this.sandboxId}/files/copy`,
        {
          from: src,
          to: dest
        }
      )

      if (!response.success) {
        throw new Error(`Failed to copy file: ${response.error}`)
      }
    } catch (error) {
      throw new Error(`Failed to copy ${src} to ${dest}: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  watch(path: string, options: WatchOptions = {}): FileSystemWatcher {
    const watcher = new SandboxFileSystemWatcher(
      this.httpClient,
      this.sandboxId,
      path,
      options
    )
    this.watchers.add(watcher)

    watcher.on('close', () => {
      this.watchers.delete(watcher)
    })

    return watcher
  }

  async mount(tree: FileSystemTree): Promise<void> {
    try {
      // Convert tree to file operations
      const operations = this.flattenTree(tree)

      const response = await this.httpClient.post(
        `/api/sandbox/${this.sandboxId}/files/mount`,
        { operations }
      )

      if (!response.success) {
        throw new Error(`Failed to mount files: ${response.error}`)
      }
    } catch (error) {
      throw new Error(`Failed to mount file tree: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  private flattenTree(tree: FileSystemTree, basePath = ''): Array<{ path: string; content?: string | Uint8Array; type: 'file' | 'directory' }> {
    const operations: Array<{ path: string; content?: string | Uint8Array; type: 'file' | 'directory' }> = []

    for (const [name, node] of Object.entries(tree)) {
      const path = basePath ? `${basePath}/${name}` : name

      if (node.file) {
        operations.push({
          path,
          content: node.file.contents,
          type: 'file'
        })
      } else if (node.directory) {
        operations.push({
          path,
          type: 'directory'
        })
        operations.push(...this.flattenTree(node.directory, path))
      }
    }

    return operations
  }

  /**
   * Close all watchers
   */
  closeAll(): void {
    for (const watcher of this.watchers) {
      watcher.close()
    }
    this.watchers.clear()
  }
}

/**
 * File system watcher implementation
 */
class SandboxFileSystemWatcher extends EventEmitter implements FileSystemWatcher {
  private httpClient: any
  private sandboxId: string
  private path: string
  private options: WatchOptions
  private isClosed = false
  private watchId?: string

  constructor(httpClient: any, sandboxId: string, path: string, options: WatchOptions) {
    super()
    this.httpClient = httpClient
    this.sandboxId = sandboxId
    this.path = path
    this.options = options

    // Start watching
    this.startWatching()
  }

  private async startWatching(): Promise<void> {
    try {
      const response = await this.httpClient.post(
        `/api/sandbox/${this.sandboxId}/files/watch`,
        {
          path: this.path,
          recursive: this.options.recursive || false
        }
      )

      if (response.success && response.data?.watchId) {
        this.watchId = response.data.watchId
        // Start listening for file change events via WebSocket
        // This would be implemented in the WebSocket client
      }
    } catch (error) {
      this.emit('error', error)
    }
  }

  close(): void {
    if (this.isClosed) return

    this.isClosed = true

    if (this.watchId) {
      // Stop watching via API
      this.httpClient.delete(
        `/api/sandbox/${this.sandboxId}/files/watch/${this.watchId}`
      ).catch(() => {
        // Ignore errors when closing
      })
    }

    this.emit('close')
    this.removeAllListeners()
  }

  ref(): FileSystemWatcher {
    // In browser environment, this is a no-op
    return this
  }

  unref(): FileSystemWatcher {
    // In browser environment, this is a no-op
    return this
  }
}